import { ProjectUser } from '@monorepo/shared/apiClient/types';
import { MenuOption } from '@monorepo/shared/componentsV2/menus/Menu';
import { RadioGroupOption } from '@monorepo/shared/componentsV2/RadioGroup';
import { permissionRoleLabels, ProjectPermissionRoles } from 'mapistry-shared';
import {
  isOrgAdmin,
  isPrimarySiteAdmin,
  NO_PERMISSION_TOOLTIP,
  projectRolesRankedByPower,
} from './sharedUtils';

const SELF_DEMOTION_TOOLTIP = 'You cannot lower your own role.';

function getRoleInfo(role: ProjectPermissionRoles): string {
  switch (role) {
    case ProjectPermissionRoles.PROJECT_PRIMARY_ADMIN:
      return 'Full access to update all site data. When a user is removed, tasks are reassigned to them.';
    case ProjectPermissionRoles.PROJECT_ADMIN:
      return 'Full access to update all site data.';
    case ProjectPermissionRoles.PROJECT_CONTRIBUTOR:
      return 'Create any site data, some limitations on editing data authored by other users.';
    case ProjectPermissionRoles.PROJECT_VIEWER:
      return 'Views all site content with read-only access.';
    case ProjectPermissionRoles.SITE_MAINTENANCE:
      return 'Receives email notifications only, cannot view any Mapistry data.';
    default:
      return '';
  }
}

// Org admins already have full org-wide access, so only the admin site roles
// are meaningful — lower roles would be overridden by their org admin power.
const orgAdminAssignableRoles = [
  ProjectPermissionRoles.PROJECT_PRIMARY_ADMIN,
  ProjectPermissionRoles.PROJECT_ADMIN,
];

const ORG_ADMIN_ROLE_TOOLTIP =
  'Organization admins can only be assigned to Site Admin or Primary Site Admin.';

// When currentUserRole is set (self-service), roles less powerful than the
// user's current one are disabled so they cannot demote themselves — a change
// that can't be undone without another admin's help. When allowedRoles is set,
// every other role is shown but disabled with an explanatory tooltip.
function buildRoleOptions({
  currentUserRole,
  allowedRoles,
}: {
  currentUserRole?: ProjectPermissionRoles;
  allowedRoles?: ProjectPermissionRoles[];
} = {}): RadioGroupOption[] {
  const currentPower = currentUserRole
    ? projectRolesRankedByPower.indexOf(currentUserRole)
    : undefined;

  return projectRolesRankedByPower.map((role, idx) => {
    const isDemotion = currentPower !== undefined && idx > currentPower;

    const isDisallowed =
      allowedRoles !== undefined && !allowedRoles.includes(role);

    let tooltip: string | undefined;
    if (isDisallowed) {
      tooltip = ORG_ADMIN_ROLE_TOOLTIP;
    } else if (isDemotion) {
      tooltip = SELF_DEMOTION_TOOLTIP;
    }

    return {
      supertitle: permissionRoleLabels[role],
      label: getRoleInfo(role),
      value: role,
      disabled: isDemotion || isDisallowed,
      tooltip,
    };
  });
}

export const inviteRoleOptions = buildRoleOptions({
  allowedRoles: projectRolesRankedByPower,
});

type ChangeRoleOptionsForUser = {
  roleOptions: RadioGroupOption[];
  disabledTooltip?: string;
};

export function getChangeRoleOptionsForUser(
  currentUser: ProjectUser,
  user: ProjectUser,
  canChangeRoles: boolean,
): ChangeRoleOptionsForUser {
  // A primary site admin must hand off primary before changing their own role,
  // otherwise the site would be left without a primary admin. This is a
  // self-service action, so it takes precedence over the general permission gate.
  const isSelf = currentUser.userId === user.userId;
  if (isSelf && isPrimarySiteAdmin(currentUser)) {
    return {
      roleOptions: [],
      disabledTooltip:
        'Another user must be designated the primary site admin before you can change your role.',
    };
  }

  // Lacking permission takes precedence over every other reason a role can't
  // be changed, so users without permission always see the same message rather
  // than more specific ones (e.g. org admin, primary site admin). canChangeRoles
  // is already false until the current user's permissions have loaded.
  if (!canChangeRoles) {
    return { roleOptions: [], disabledTooltip: NO_PERMISSION_TOOLTIP };
  }

  // A primary site admin's role can't be changed directly — a different user
  // must be designated the primary site admin first, which demotes this one.
  if (isPrimarySiteAdmin(user)) {
    return {
      roleOptions: [],
      disabledTooltip:
        "Another user must be designated the primary site admin before this user's role can be changed.",
    };
  }

  // Org admins can be designated a site admin role directly, without first
  // removing their org admin status. Lower roles are shown but disabled.
  if (isOrgAdmin(user)) {
    return {
      roleOptions: buildRoleOptions({ allowedRoles: orgAdminAssignableRoles }),
    };
  }

  return {
    roleOptions: buildRoleOptions({
      currentUserRole: isSelf ? currentUser.projectRoles : undefined,
    }),
  };
}

type ChangeRoleActionParams = {
  disabledTooltip?: string;
  onChangeRole: () => void;
};

export function getChangeRoleAction({
  disabledTooltip,
  onChangeRole,
}: ChangeRoleActionParams): MenuOption {
  return {
    displayName: 'Change role',
    onClick: onChangeRole,
    disabled: !!disabledTooltip,
    tooltip: disabledTooltip,
  };
}
